import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID, SimpleChanges, ViewChild } from '@angular/core';
import { Icons } from '../../../../shared/helpers/icons';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { ISchedule } from '../../../../shared/models/schedule';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { fileUploadValidator } from '../../../../shared/helpers/fileUploadValidation';
import { EScheduleTypes } from '../../../../shared/models/eop_types.enum';
import { ScheduleService } from '../../../../shared/services/schedule.service';
import { DateUtils } from '../../../../shared/helpers/dateUtils';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { NoEmojiDirective } from '../../../../shared/helpers/noEmoji.directive';

@Component({
  selector: 'event-edit',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ReactiveFormsModule,
    SpinnerComponent,
    NoEmojiDirective
  ],
  templateUrl: './event-edit.component.html',
  styleUrl: './event-edit.component.css'
})
export class EventEditComponent implements AfterViewInit, OnInit {
  @ViewChild('datepickerStartInput') datepickerStartInput!: ElementRef;
  // @ViewChild('datepickerEndInput') datepickerEndInput!: ElementRef;

  icons = Icons;

  eventForm: FormGroup = new FormGroup({});
  scheduleTypes = EScheduleTypes;
  loading: boolean = false;

  @Input() eventToEdit: ISchedule | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private scheduleService: ScheduleService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eventToEdit'] && this.eventToEdit) {
      this.initializeForm(); // Reinitialize the form with new `eventToEdit` data
    }
  }

  async ngAfterViewInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      // Dynamically import the Datepicker to avoid server-side loading
      const { Datepicker } = await import('flowbite-datepicker');
      if (this.datepickerStartInput) {
        new Datepicker(this.datepickerStartInput.nativeElement, {
          autohide: true,
          format: 'mm/dd/yyyy'
        });
        // display the old date on input field if eventToEdit existed
        if (this.eventToEdit) {
          this.datepickerStartInput.nativeElement.value = DateUtils.formatDateForDisplay(this.eventToEdit.start_date);
        }
        this.datepickerStartInput.nativeElement.addEventListener('changeDate', (event: any) => {
          const formattedDate = DateUtils.formatDateForBackend(event.target.value);
          this.eventForm.get('start_date')?.setValue(formattedDate);
        });
      }
      // if (this.datepickerEndInput) {
      //   new Datepicker(this.datepickerEndInput.nativeElement, {
      //     autohide: true,
      //     format: 'mm/dd/yyyy'
      //   });
      //   // display the old date if eventToEdit existed
      //   if (this.eventToEdit) {
      //     this.datepickerEndInput.nativeElement.value = this.formatDateForDisplay(this.eventToEdit.end_date);
      //   }
      //   this.datepickerEndInput.nativeElement.addEventListener('changeDate', (event: any) => {
      //     const formattedDate = this.formatDateForBackend(event.target.value);
      //       this.eventForm.get('end_date')?.setValue(formattedDate);
      //   });
      // }
    }
  }

  private initializeForm(): void {
    this.eventForm = new FormGroup({
      name: new FormControl(this.eventToEdit?.name || '', Validators.required),
      type_id: new FormControl(this.eventToEdit?.type_id || EScheduleTypes.Training, Validators.required), // Default to Training if not provided
      start_date: new FormControl(this.eventToEdit?.start_date || '', Validators.required),
      time: new FormControl(this.eventToEdit?.time || '08:00', Validators.required),
      duration: new FormControl(this.eventToEdit?.duration || '', Validators.required),
      details: new FormControl(this.eventToEdit?.details || '', Validators.required),
      files: new FormControl<File[]>([], fileUploadValidator()),
    });
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      this.eventForm.get('files')?.setValue(files);
      this.eventForm.get('files')?.markAsDirty();
      this.eventForm.get('files')?.updateValueAndValidity();
    }
  }

  saveEdit(): void {
    if (this.eventForm.valid && this.eventToEdit?.id) {
      this.loading = true;
      const eventForm = new FormData();
      const currentValues = this.eventForm.value;
  
      // Compare and append only changed fields
      Object.keys(currentValues).forEach((key) => {
        const originalValue = (this.eventToEdit as any)[key];
        const currentValue = currentValues[key];
  
        if (key === 'files') {
          if (currentValue && currentValue.length > 0) {
            currentValue.forEach((file: File) => {
              if (file instanceof Blob) {
                eventForm.append(key, file, file.name);
              } else {
                console.error("Invalid file type detected:", file);
              }
            });
          }
          return; // Skip comparison for files
        }
  
        // Convert to string for comparison (if needed)
        const isChanged = String(originalValue) !== String(currentValue);
  
        if (isChanged) {
          eventForm.append(key, currentValue);
        }
      });
      
      this.scheduleService.editSchedule(String(this.eventToEdit.id), eventForm).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.onSave.emit({ type: 'success', message: 'This occurred after updating an event.' });
          }
          console.log(response);
        },
        error: (error) => {
          this.loading = false;
          this.onSave.emit({ type: 'unsuccess', message: error.message });
        }
      })
    }
  }

  closeEdit(): void {
    this.onClose.emit();
  }
}
